import {
  Alert,
  Button,
  Drawer,
  Form,
  notification,
  Space,
  Spin,
  Table,
} from 'antd'
import { useEffect, useRef, useState } from 'react'
import Card from 'components/Card'
import { DRAFT_SURVEY_TYPE } from 'domain/PortCall/constants'
import DraftDifferenceSection from './DraftDifferenceSection'
import DraftInputSection from './DraftInputSection'
import featureCodes from 'constants/featureCodes'
import getClassNames from 'classnames'
import MoreInfoModal from 'components/MoreInfoModal'
import propTypes from 'global/propTypes'
import PropTypes from 'prop-types'
import { QuestionCircleOutlined } from '@ant-design/icons'
import React from 'react'
import Text from 'components/Text'
import useDraftValidation from './useDraftValidation'
import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'
import { useTranslation } from 'react-i18next'
import useWaterDensityValidation from './useWaterDensityValidation'

import './vesselDraftsTable.less'
import { calculateVesselDraftDifference } from './util'
import WaterDensitySection from './WaterDensitySection'

const VesselDraftsTable = ({
  draftSurveySaveError,
  drafts,
  isArrivalDrafts = false,
  isLoading,
  isPortCallClosed,
  isSavingDraftSurvey,
  onSavePortCallDraftSurvey,
  setSavePortCallDraftSurveyError,
  waterDensity,
}) => {
  const { t } = useTranslation()
  const { getDraftValidationRules } = useDraftValidation()
  const { getWaterDensityValidationRules } = useWaterDensityValidation()

  const errorRef = useRef(null)

  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false)

  const [form] = Form.useForm()

  const hasVesselDrafts = drafts !== null

  // Drawer handlers
  const openEditDrawer = () => {
    setSavePortCallDraftSurveyError()

    if (drafts) {
      // Set initial form values
      form.setFieldsValue({
        calculated: {
          aft: drafts.calculated.aft.toFixed(2),
          forward: drafts.calculated.forward.toFixed(2),
          midship: drafts.calculated.midship.toFixed(2),
        },
        visual: {
          aft: drafts.visual.aft.toFixed(2),
          forward: drafts.visual.forward.toFixed(2),
          midship: drafts.visual.midship.toFixed(2),
        },
        waterDensity:
          waterDensity !== null && waterDensity !== undefined
            ? waterDensity.toFixed(3)
            : '',
      })
    }

    setEditDrawerOpen(true)
  }

  // Feature flag: controls whether the vessel draft edit action is available
  const isVesselDraftEditEnabled = useFeatureAccessControlEnabled(
    featureCodes.EDIT_VESSEL_DRAFT
  )

  const closeEditDrawer = () => {
    setEditDrawerOpen(false)
    form.resetFields()
  }

  // Modal handlers
  const openHelpModal = () => setIsHelpModalVisible(true)
  const closeHelpModal = () => setIsHelpModalVisible(false)

  // Form submit handler
  const handleSubmit = values => {
    const normalize = v => (v === '' || v === undefined ? null : Number(v))

    const payload = {
      calculated: {
        aft: normalize(values.calculated.aft),
        forward: normalize(values.calculated.forward),
        midship: normalize(values.calculated.midship),
      },
      visualInspection: {
        aft: normalize(values.visual.aft),
        forward: normalize(values.visual.forward),
        midship: normalize(values.visual.midship),
      },

      waterDensity: normalize(values.waterDensity),
    }

    const type = isArrivalDrafts
      ? DRAFT_SURVEY_TYPE.ARRIVAL
      : DRAFT_SURVEY_TYPE.DEPARTURE

    const handleSuccess = () => {
      closeEditDrawer()

      const successMessage = isArrivalDrafts
        ? t('portCall.draftSurvey.arrivalSaveSuccess')
        : t('portCall.draftSurvey.departureSaveSuccess')

      notification.success({
        message: successMessage,
      })
    }

    onSavePortCallDraftSurvey({
      onSuccess: handleSuccess,
      payload,
      type,
    })
  }

  // Determine if Submit button should be disabled
  const isSubmitDisabled = () => {
    const hasErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0)

    const values = form.getFieldsValue(true)

    // Check if all required draft fields have values (water density is optional)
    const allDraftFieldsFilled =
      values.visual?.forward &&
      values.visual?.midship &&
      values.visual?.aft &&
      values.calculated?.forward &&
      values.calculated?.midship &&
      values.calculated?.aft

    // Disable Submit button when there are any field validation errors or api call is ongoing
    return hasErrors || !allDraftFieldsFilled || isSavingDraftSurvey
  }

  const tableData = hasVesselDrafts
    ? [
        {
          aft: drafts.visual.aft.toFixed(2),
          forward: drafts.visual.forward.toFixed(2),
          key: t('portCall.draftSurvey.visual'),
          midship: drafts.visual.midship.toFixed(2),
          rowTitle: t('portCall.draftSurvey.visual'),
        },
        {
          aft: drafts.calculated.aft.toFixed(2),
          forward: drafts.calculated.forward.toFixed(2),
          key: t('portCall.draftSurvey.calculated'),
          midship: drafts.calculated.midship.toFixed(2),
          rowTitle: t('portCall.draftSurvey.calculated'),
        },
        {
          aft: calculateVesselDraftDifference(
            drafts.visual.aft,
            drafts.calculated.aft
          ),
          forward: calculateVesselDraftDifference(
            drafts.visual.forward,
            drafts.calculated.forward
          ),
          key: t('portCall.draftSurvey.difference'),
          midship: calculateVesselDraftDifference(
            drafts.visual.midship,
            drafts.calculated.midship
          ),
          rowTitle: t('portCall.draftSurvey.difference'),
        },
      ]
    : []

  const tableColumns = hasVesselDrafts
    ? [
        { dataIndex: 'rowTitle', key: 'rowTitle', title: '' },
        {
          align: 'center',
          dataIndex: 'forward',
          key: 'forward',
          title: `${t('portCall.draftSurvey.forward')} ${t(
            'portCall.draftSurvey.mUnit'
          )}`,
        },
        {
          align: 'center',
          dataIndex: 'midship',
          key: 'midship',
          title: `${t('portCall.draftSurvey.midship')} ${t(
            'portCall.draftSurvey.mUnit'
          )}`,
        },
        {
          align: 'center',
          dataIndex: 'aft',
          key: 'aft',
          title: `${t('portCall.draftSurvey.aft')} ${t(
            'portCall.draftSurvey.mUnit'
          )}`,
        },
      ]
    : []

  const renderTableFooter = () => (
    <div className="vessel-drafts-table__footer--container">
      <div className="vessel-drafts-table__footer--title">
        <Text>{t('portCall.draftSurvey.waterDensity')}</Text>
      </div>
      <div className="vessel-drafts-table__footer--value">
        <Text align="center">
          {waterDensity === null
            ? t('portCall.draftSurvey.notRecorded')
            : `${waterDensity.toFixed(3)} ${t('portCall.draftSurvey.t/m3')}`}
        </Text>
      </div>
    </div>
  )

  const getRowClassName = (record, index) =>
    getClassNames({
      'vessel-drafts-table__row--background': index % 2 === 0,
    })

  // Draft difference calculator
  const getDifferenceIfValid = (visualPath, calculatedPath) => {
    const visualErrors = form.getFieldError(visualPath)
    const calculatedErrors = form.getFieldError(calculatedPath)

    if (visualErrors.length === 0 && calculatedErrors.length === 0) {
      const visualValue = form.getFieldValue(visualPath)
      const calculatedValue = form.getFieldValue(calculatedPath)

      return calculateVesselDraftDifference(visualValue, calculatedValue)
    }

    return ''
  }

  // Scroll to error alert when draft survey save error occurs
  useEffect(() => {
    if (draftSurveySaveError && errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [draftSurveySaveError])

  return (
    <>
      <Card
        headerActionButton={
          isVesselDraftEditEnabled
            ? {
                disabled: isPortCallClosed,
                label: t('portCall.draftSurvey.edit'),
                onClick: openEditDrawer,
              }
            : undefined
        }
        title={
          isArrivalDrafts
            ? t('portCall.draftSurvey.arrivalVesselDrafts')
            : t('portCall.draftSurvey.departureVesselDrafts')
        }
      >
        {hasVesselDrafts ? (
          <>
            <div>
              <Alert
                className="vessel-drafts-table__warning"
                showIcon
                type="warning"
                message={t(
                  'portCall.draftSurvey.consultVesselBeforeUsingDrafts'
                )}
              />
            </div>

            <Table
              bordered
              className="vessel-drafts-table__table--container"
              columns={tableColumns}
              dataSource={tableData}
              footer={renderTableFooter}
              pagination={false}
              rowClassName={getRowClassName}
              size="middle"
            />
          </>
        ) : isLoading ? (
          <div className="vessel-drafts-table__spin">
            <Spin />
          </div>
        ) : (
          <Text align="center">{t('portCall.draftSurvey.notRecorded')}</Text>
        )}
      </Card>

      {/* Edit Drawer */}
      <Drawer
        closable={!isSavingDraftSurvey}
        maskClosable={!isSavingDraftSurvey}
        onClose={isSavingDraftSurvey ? undefined : closeEditDrawer}
        open={editDrawerOpen}
        placement="right"
        size="large"
        title={
          <div className="vessel-drafts-table__drawer-title">
            <Text className="vessel-drafts-table__drawer-title-text">
              {isArrivalDrafts
                ? t('portCall.draftSurvey.arrivalVesselDrafts')
                : t('portCall.draftSurvey.departureVesselDrafts')}
            </Text>

            <QuestionCircleOutlined
              className="vessel-drafts-table__drawer-help"
              onClick={openHelpModal}
            />
          </div>
        }
      >
        {/* Loading overlay when saving */}
        {isSavingDraftSurvey && (
          <div className="vessel-drafts-table__loading-overlay">
            <Spin size="large" />
          </div>
        )}
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Visual Inspection */}
          <DraftInputSection
            disabled={isSavingDraftSurvey}
            fieldPrefix="visual"
            title={t('portCall.draftSurvey.visualInspection')}
            validationRules={getDraftValidationRules()}
          />

          {/* Calculated */}
          <DraftInputSection
            disabled={isSavingDraftSurvey}
            fieldPrefix="calculated"
            title={t('portCall.draftSurvey.calculated')}
            validationRules={getDraftValidationRules()}
          />

          {/* Difference */}
          <DraftDifferenceSection getDifferenceIfValid={getDifferenceIfValid} />

          {/* Water Density */}
          <WaterDensitySection
            disabled={isSavingDraftSurvey}
            isArrivalDrafts={isArrivalDrafts}
            validationRules={getWaterDensityValidationRules()}
          />

          {/* Form actions */}
          <Space style={{ marginTop: 16 }}>
            <Button disabled={isSavingDraftSurvey} onClick={closeEditDrawer}>
              {t('portCall.draftSurvey.cancel')}
            </Button>

            <Form.Item noStyle shouldUpdate>
              {() => (
                <Button
                  disabled={isSubmitDisabled()}
                  htmlType="submit"
                  type="primary"
                >
                  {t('portCall.draftSurvey.submit')}
                </Button>
              )}
            </Form.Item>
          </Space>

          {/* Error message */}
          {draftSurveySaveError && (
            <div ref={errorRef} style={{ marginTop: 8 }}>
              <Alert
                banner
                message={t(draftSurveySaveError)}
                showIcon
                type="error"
              />
            </div>
          )}
        </Form>
      </Drawer>

      {/* Help Modal */}
      <MoreInfoModal
        isVisible={isHelpModalVisible}
        onClose={closeHelpModal}
        title={t('portCall.draftSurvey.modalInfo')}
      >
        <div className="vessel-drafts-help">
          <div className="vessel-drafts-help__intro">
            <Text>{t('portCall.draftSurvey.helpIntro')}</Text>
          </div>

          <div className="vessel-drafts-help__section">
            <h3>{t('portCall.draftSurvey.visualInspection')}</h3>
            <Text>{t('portCall.draftSurvey.visualInspectionHelp')}</Text>
          </div>

          <div className="vessel-drafts-help__section">
            <h3>{t('portCall.draftSurvey.calculated')}</h3>
            <Text>{t('portCall.draftSurvey.calculatedHelp')}</Text>
          </div>

          <div className="vessel-drafts-help__section">
            <h3>{t('portCall.draftSurvey.waterDensity')}</h3>
            <Text>{t('portCall.draftSurvey.waterDensityHelp')}</Text>
          </div>
        </div>
      </MoreInfoModal>
    </>
  )
}

VesselDraftsTable.propTypes = {
  draftSurveySaveError: PropTypes.string,
  drafts: propTypes.drafts,
  isArrivalDrafts: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isPortCallClosed: PropTypes.bool.isRequired,
  isSavingDraftSurvey: PropTypes.bool.isRequired,
  onSavePortCallDraftSurvey: PropTypes.func.isRequired,
  setSavePortCallDraftSurveyError: PropTypes.func,
  waterDensity: PropTypes.number,
}

export default VesselDraftsTable
